import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Trash2, Upload } from 'lucide-react';
import { documentosVentaService } from '../../../services/api';

const TIPOS = [
  { id: 'ficha_tecnica', label: 'Ficha técnica' },
  { id: 'catalogo', label: 'Catálogo' },
  { id: 'manual', label: 'Manual' },
  { id: 'condiciones', label: 'Condiciones de venta' },
  { id: 'otro', label: 'Otro' }
];

const ENTRADA = 'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const etiquetaTipo = (tipo) => TIPOS.find((t) => t.id === tipo)?.label || tipo;

const fmtTamano = (bytes) => (bytes >= 1024 * 1024
  ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const VACIO = { titulo: '', tipo: 'ficha_tecnica', descripcion: '', archivo: null };

// Sin maquinaId son los documentos generales (catalogo completo, condiciones).
const DocumentosPanel = ({ maquinaId = null }) => {
  const [documentos, setDocumentos] = useState([]);
  const [form, setForm] = useState(VACIO);
  const [claveArchivo, setClaveArchivo] = useState(0);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    try {
      const res = await documentosVentaService.listar({ maquinaId });
      setDocumentos(res.data || []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los documentos');
    }
  }, [maquinaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const ejecutar = async (accion) => {
    setOcupado(true);
    setError('');
    try {
      await accion();
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo completar la acción');
    } finally {
      setOcupado(false);
    }
  };

  const subir = () => ejecutar(async () => {
    await documentosVentaService.subir({ ...form, titulo: form.titulo.trim(), maquinaId });
    setForm(VACIO);
    setClaveArchivo((k) => k + 1);
  });

  const descargar = (doc) => ejecutar(async () => {
    const blob = await documentosVentaService.descargar(doc.id);
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = doc.nombre_original;
    enlace.click();
    URL.revokeObjectURL(url);
  });

  const puedeSubir = form.archivo && form.titulo.trim() && !ocupado;

  return (
    <div>
      {error && <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {documentos.length === 0 && <p className="text-sm text-gray-500 mb-3">Todavía no hay documentos.</p>}
      <ul className="space-y-2 mb-4">
        {documentos.map((d) => (
          <li key={d.id} className="flex items-start gap-3 rounded-lg border border-gray-200 px-3 py-2">
            <FileText size={18} className="mt-0.5 text-gray-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 truncate">{d.titulo}</div>
              <div className="text-xs text-gray-500">{etiquetaTipo(d.tipo)} · {fmtTamano(d.tamano_bytes)} · {d.nombre_original}</div>
              {d.descripcion && <div className="text-xs text-gray-500 mt-0.5">{d.descripcion}</div>}
            </div>
            <button type="button" title="Descargar" aria-label="Descargar" disabled={ocupado} onClick={() => descargar(d)} className="text-gray-500 hover:text-blue-600"><Download size={16} /></button>
            <button type="button" title="Desactivar" aria-label="Desactivar" disabled={ocupado} onClick={() => ejecutar(() => documentosVentaService.desactivar(d.id))} className="text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
          </li>
        ))}
      </ul>

      <div className="rounded-lg border border-dashed border-gray-300 p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block md:col-span-2">
          <span className="block text-xs font-medium text-gray-600 mb-1">Título *</span>
          <input className={ENTRADA} value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))} />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Tipo</span>
          <select className={ENTRADA} value={form.tipo} onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}>
            {TIPOS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </label>
        <label className="block md:col-span-3">
          <span className="block text-xs font-medium text-gray-600 mb-1">Descripción</span>
          <input className={ENTRADA} value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} />
          <span className="block text-[11px] text-gray-400 mt-0.5">El bot la lee para saber cuándo enviar el documento. Ej. "Medidas, consumo y capacidad".</span>
        </label>
        <div className="md:col-span-3 flex items-center gap-3">
          <input
            key={claveArchivo}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.pptx,.jpg,.jpeg,.png"
            onChange={(e) => setForm((f) => ({ ...f, archivo: e.target.files?.[0] || null }))}
            className="text-sm flex-1"
          />
          <button type="button" disabled={!puedeSubir} onClick={subir} className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
            <Upload size={16} /> {ocupado ? 'Procesando...' : 'Subir'}
          </button>
        </div>
        <p className="md:col-span-3 text-[11px] text-gray-400">PDF, Word, Excel, PowerPoint, JPG o PNG de hasta 10 MB.</p>
      </div>
    </div>
  );
};

export default DocumentosPanel;
